import React, { useCallback, useEffect, useState } from 'react';
import { makeStyles, Typography, Grid, Button, Theme, Hidden, Box } from '@material-ui/core';
import CircularProgress from '@material-ui/core/CircularProgress';
import LocalVideoPreview from './LocalVideoPreview/LocalVideoPreview';
import SettingsMenu from './SettingsMenu/SettingsMenu';
import ToggleAudioButton from '../../Buttons/ToggleAudioButton/ToggleAudioButton';
import ToggleVideoButton from '../../Buttons/ToggleVideoButton/ToggleVideoButton';
import { useAppState, ITwilioResponse } from '../../../state';
import useChatContext from '../../../hooks/useChatContext/useChatContext';
import useVideoContext from '../../../hooks/useVideoContext/useVideoContext';
import MediaErrorSnackbar from '../MediaErrorSnackbar/MediaErrorSnackbar';

const useStyles = makeStyles((theme: Theme) => ({
  heading: {
    display: 'flex',
    justifyContent: 'center',
    minHeight: 40,
    padding: theme.spacing(2),
  },
  contentWrapper: {
    flexGrow: 1,
    overflow: 'auto',
    boxFlex: 1,
  },
  contentBlock: {
    minHeight: '100%',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  marginTop: {
    marginTop: '1em',
  },
  deviceButton: {
    // width: '100%',
    // border: '2px solid #aaa',
    margin: '1em 0',
  },
  localPreviewContainer: {
    [theme.breakpoints.down('sm')]: {
      padding: '0 0.5em',
    },
  },
  joinButtons: {
    display: 'flex',
    flexDirection:'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    width: '100%',
    [theme.breakpoints.down('sm')]: {
      flexDirection:'row',
      width: '100%',
      '& button': {
        margin: '0.5em',
      },
    },
    '& .MuiButton-root': {
      width: 150,
      borderRadius: 50,
      marginBottom: theme.spacing(2)
    },
  },
  mobileButtonBar: {
    display: 'flex',
    justifyContent: 'space-between',
    [theme.breakpoints.down('sm')]: {
      display: 'flex',
      justifyContent: 'space-between',
      margin: '1em 0',
    },
  },
  mobileButton: {
    margin: '1em 0',
  },
}));

interface DeviceSelectionScreenProps {
  twilioObject: ITwilioResponse;
}

export default function DeviceSelectionScreen({
  twilioObject: { user_identity, room_name, token, user_type },
}: DeviceSelectionScreenProps) {
  const classes = useStyles();
  const { connect: chatConnect } = useChatContext();
  const { getRoom } = useAppState();
  const {
    connect: videoConnect,
    isAcquiringLocalTracks,
    isConnecting,
    getAudioAndVideoTracks,
    onError,
  } = useVideoContext();
  const disableButtons = isAcquiringLocalTracks || isConnecting;
  const [mediaError, setMediaError] = useState<Error>();
  const roomNotCreated = 'room_not_created';

  useEffect(() => {
    if (!mediaError) {
      getAudioAndVideoTracks().catch(error => {
        console.log('Error acquiring local media:');
        console.dir(error);
        setMediaError(error);
      });
    }
  }, []);

  const connectVideo = useCallback(() => {
    videoConnect(token);
    process.env.REACT_APP_DISABLE_TWILIO_CONVERSATIONS !== 'true' && chatConnect(token);
    //chatConnect
  }, [chatConnect, token, videoConnect]);

  const handleJoin = useCallback(() => {
    if (user_type === 'host') {
      connectVideo();
    } else {
      getRoom(room_name)
        .then(roomStatus => {
          if (roomStatus) connectVideo();
          else onError(new Error('Host disconnected from room'));
        })
        .catch(() => {
          onError(new Error('Host disconnected from room'));
        });
    }
  }, [connectVideo, getRoom, onError, room_name, user_type]);

  if (isConnecting) {
    return (
      <Grid container justifyContent="center" alignItems="center" direction="column" style={{ height: '100%' }}>
        <div>
          <CircularProgress variant="indeterminate" />
        </div>
        <div>
          <Typography variant="body2" style={{ fontWeight: 'bold', fontSize: '16px' }}>
            Joining Meeting
          </Typography>
        </div>
      </Grid>
    );
  }

  return (
    <>
      <MediaErrorSnackbar error={mediaError} />
      {/* <Typography variant="h5" className={classes.heading}>
        Join {room_name}
      </Typography> */}
      <div className={classes.contentWrapper}>
        <div className={classes.contentBlock}>
        <Grid container>
          <Grid item md={1}></Grid>
          <Grid item md={7} sm={12} xs={12}>
            <div className={classes.localPreviewContainer}>
              <LocalVideoPreview identity={user_identity} />
            </div>
            <div className={classes.mobileButtonBar}>
            <Hidden smDown>
                <Box>
                  <ToggleAudioButton className={classes.deviceButton} disabled={disableButtons} />
                  <ToggleVideoButton className={classes.deviceButton} disabled={disableButtons} />
                </Box>
              </Hidden>
              <Hidden mdUp>
                <ToggleAudioButton className={classes.mobileButton} disabled={disableButtons} />
                <ToggleVideoButton className={classes.mobileButton} disabled={disableButtons} />
              </Hidden>
              <SettingsMenu mobileButtonClass={classes.mobileButton} />
            </div>
          </Grid>
          <Grid item md={3} sm={12} xs={12}>
            <div className={classes.joinButtons}>
              <Button
                variant="contained"
                color="primary"
                data-cy-join-now
                onClick={handleJoin}
                disabled={disableButtons || token === roomNotCreated}
              >
                Join Now
              </Button>
              <Button variant="outlined" color="primary">
                Cancel
              </Button>
            </div>
          </Grid>
          <Grid item md={1}></Grid>
        </Grid>
        </div>
      </div>
    </>
  );
}
